// TMOS Business Performance Dashboard Overview Page
// Date: January 13, 2026
// Purpose: Executive dashboard overview with plan cards and summary statistics

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/state/language/LanguageProvider';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { PlanCard } from '@/components/business-performance/PlanCard';
import { PerformanceTabs } from '@/components/business-performance/PerformanceTabs';
import { HealthIndicator } from '@/components/business-performance/HealthIndicator';
import { businessPerformanceService, DashboardFilters } from '@/services/businessPerformanceService';
import { DashboardOverview } from '@/types/businessPerformance';
import { Plan } from '@/types/plan';
import toast from 'react-hot-toast';
import {
  ChartBarIcon,
  FunnelIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

export const DashboardOverviewPage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filters
  const [filters, setFilters] = useState<DashboardFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [planTypeFilter, setPlanTypeFilter] = useState<'STRATEGIC' | 'ANNUAL' | 'QUARTERLY' | ''>('');
  const [planYearFilter, setPlanYearFilter] = useState<string>('');

  useEffect(() => {
    loadDashboardData();
  }, [filters]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const data = await businessPerformanceService.getDashboardOverview(filters);
      setOverview(data);
    } catch (error: any) {
      console.error('Error loading dashboard overview:', error);
      toast.error(error.message || t('businessPerformance.errorLoadingDashboard'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadDashboardData();
  };

  const handleFilterChange = () => {
    const newFilters: DashboardFilters = {};
    if (planTypeFilter) {
      newFilters.plan_type = planTypeFilter as 'STRATEGIC' | 'ANNUAL' | 'QUARTERLY';
    }
    if (planYearFilter) {
      newFilters.plan_year = parseInt(planYearFilter, 10);
    }
    setFilters(newFilters);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setPlanTypeFilter('');
    setPlanYearFilter('');
    setFilters({});
    setShowFilters(false);
  };

  const handlePlanClick = (planId: string) => {
    navigate(`/performance/plans/${planId}`);
  };

  if (loading && !overview) {
    return (
      <div className="min-h-screen bg-[var(--color-secondary-bg)]">
        <div className="container mx-auto px-2 py-2">
          <div className="flex items-center justify-center h-64">
            <div className="text-[var(--color-secondary-text)]">Loading dashboard...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="container mx-auto px-2 py-2">
        {/* Header */}
        <div className="mb-2">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-xl font-bold text-[var(--color-surface-text)] mb-1">
                {t('businessPerformance.dashboardOverview') || 'Performance Dashboard'}
              </h1>
              <p className="text-sm text-[var(--color-secondary-text)]">
                {t('businessPerformance.dashboardOverviewDescription') ||
                  'Personal Business, Function, and Portfolio performance at your hierarchy'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2"
              >
                <FunnelIcon className="w-4 h-4" />
                {t('common.filters') || 'Filters'}
              </Button>
              <Button
                variant="outline"
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2"
              >
                <ArrowPathIcon className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                {t('common.refresh') || 'Refresh'}
              </Button>
            </div>
          </div>
        </div>

        <PerformanceTabs />

        <div className="mb-2">
          {/* Filters Panel */}
          {showFilters && (
            <Card variant="outline" className="mb-4">
              <CardContent className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                      {t('businessPerformance.planType') || 'Plan Type'}
                    </label>
                    <select
                      value={planTypeFilter}
                      onChange={(e) => setPlanTypeFilter(e.target.value as any)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-[var(--color-surface-text)]"
                    >
                      <option value="">{t('common.all') || 'All'}</option>
                      <option value="STRATEGIC">{t('businessPerformance.strategic') || 'Strategic'}</option>
                      <option value="ANNUAL">{t('businessPerformance.annual') || 'Annual'}</option>
                      <option value="QUARTERLY">{t('businessPerformance.quarterly') || 'Quarterly'}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                      {t('businessPerformance.planYear') || 'Plan Year'}
                    </label>
                    <input
                      type="number"
                      value={planYearFilter}
                      onChange={(e) => setPlanYearFilter(e.target.value)}
                      placeholder={new Date().getFullYear().toString()}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-[var(--color-surface-text)]"
                    />
                  </div>
                  <div className="flex items-end gap-2">
                    <Button onClick={handleFilterChange} variant="primary">
                      {t('common.apply') || 'Apply'}
                    </Button>
                    <Button onClick={clearFilters} variant="outline">
                      {t('common.clear') || 'Clear'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Summary Statistics */}
        {overview && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card variant="outline">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[var(--color-secondary-text)] mb-1">
                      {t('businessPerformance.totalPlans') || 'Total Plans'}
                    </p>
                    <p className="text-2xl font-semibold text-[var(--color-surface-text)]">
                      {overview.total_plans}
                    </p>
                  </div>
                  <ChartBarIcon className="w-8 h-8 text-[var(--color-info)]" />
                </div>
              </CardContent>
            </Card>

            <Card variant="outline">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[var(--color-secondary-text)] mb-1">
                      {t('businessPerformance.plansOnTrack') || 'Plans On Track'}
                    </p>
                    <div className="flex items-center gap-2">
                      <p className="text-2xl font-semibold text-[var(--color-surface-text)]">
                        {overview.plans_on_track}
                      </p>
                      <HealthIndicator status="GREEN" size="sm" />
                    </div>
                  </div>
                  <ChartBarIcon className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card variant="outline">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[var(--color-secondary-text)] mb-1">
                      {t('businessPerformance.plansAtRisk') || 'Plans At Risk'}
                    </p>
                    <div className="flex items-center gap-2">
                      <p className="text-2xl font-semibold text-[var(--color-surface-text)]">
                        {overview.plans_at_risk}
                      </p>
                      <HealthIndicator status="YELLOW" size="sm" />
                    </div>
                  </div>
                  <ChartBarIcon className="w-8 h-8 text-yellow-500" />
                </div>
              </CardContent>
            </Card>

            <Card variant="outline">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[var(--color-secondary-text)] mb-1">
                      {t('businessPerformance.plansOffTrack') || 'Plans Off Track'}
                    </p>
                    <div className="flex items-center gap-2">
                      <p className="text-2xl font-semibold text-[var(--color-surface-text)]">
                        {overview.plans_off_track}
                      </p>
                      <HealthIndicator status="RED" size="sm" />
                    </div>
                  </div>
                  <ChartBarIcon className="w-8 h-8 text-red-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Objectives Summary */}
        {overview && (
          <Card variant="outline" className="mb-6">
            <CardContent className="p-4">
              <h2 className="text-lg font-semibold text-[var(--color-surface-text)] mb-4">
                {t('businessPerformance.objectivesSummary') || 'Objectives Summary'}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-[var(--color-secondary-text)] mb-1">
                    {t('businessPerformance.totalObjectives') || 'Total Objectives'}
                  </p>
                  <p className="text-xl font-semibold text-[var(--color-surface-text)]">
                    {overview.total_objectives}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-[var(--color-secondary-text)] mb-1">
                    {t('businessPerformance.objectivesOnTrack') || 'On Track'}
                  </p>
                  <div className="flex items-center gap-2">
                    <p className="text-xl font-semibold text-[var(--color-surface-text)]">
                      {overview.objectives_on_track}
                    </p>
                    <HealthIndicator status="GREEN" size="sm" />
                  </div>
                </div>
                <div>
                  <p className="text-sm text-[var(--color-secondary-text)] mb-1">
                    {t('businessPerformance.objectivesAtRisk') || 'At Risk'}
                  </p>
                  <div className="flex items-center gap-2">
                    <p className="text-xl font-semibold text-[var(--color-surface-text)]">
                      {overview.objectives_at_risk}
                    </p>
                    <HealthIndicator status="YELLOW" size="sm" />
                  </div>
                </div>
                <div>
                  <p className="text-sm text-[var(--color-secondary-text)] mb-1">
                    {t('businessPerformance.objectivesOffTrack') || 'Off Track'}
                  </p>
                  <div className="flex items-center gap-2">
                    <p className="text-xl font-semibold text-[var(--color-surface-text)]">
                      {overview.objectives_off_track}
                    </p>
                    <HealthIndicator status="RED" size="sm" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Plans Grid */}
        {overview && (
          <div>
            <h2 className="text-lg font-semibold text-[var(--color-surface-text)] mb-4">
              {t('businessPerformance.plans') || 'Plans'}
            </h2>
            {overview.plans && overview.plans.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {overview.plans.map((plan) => (
                  <PlanCard
                    key={plan.id}
                    plan={{
                      id: plan.id,
                      name: plan.name,
                      tenant_id: '',
                      plan_type: plan.plan_type as any,
                      plan_year: undefined,
                      plan_quarter: undefined,
                      start_date: undefined,
                      end_date: undefined,
                      status: plan.status as any,
                      description: undefined,
                      created_at: new Date().toISOString(),
                      updated_at: new Date().toISOString()
                    } as unknown as Plan}
                    health={plan.health}
                    onClick={handlePlanClick}
                  />
                ))}
              </div>
            ) : (
              <Card variant="outline">
                <CardContent className="p-8 text-center">
                  <ChartBarIcon className="w-12 h-12 text-[var(--color-secondary-text)] mx-auto mb-4 opacity-50" />
                  <p className="text-[var(--color-secondary-text)]">
                    {t('businessPerformance.noPlansFound') || 'No plans found. Create a plan to get started.'}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

